import * as React from 'react';

import { translate } from '~l10n/client';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { Action } from '~shared/components/ActionMenu';
import { IconMerge } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import { DispatchMergeQuizDialogAction } from '~teacher/components/quizzes/MergeQuizDialog/useMergeQuizDialog';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions';
import { useWorkspacePermissions } from '~teacher/hook/workspaces';
import { useIsWorkspaceReadOnly } from '~teacher/hook/workspaces/settings';
import {
  UseMergeWorkspaceQuizzesMutation,
  useMergeWorkspaceQuizzes,
} from '~teacher/hook/workspaces/useMergeWorkspaceQuizzes';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceQuiz } from '../../../../../api/workspaces/quiz/types';

function createOnMerge(
  workspaceItems: WorkspaceItem[],
  mergeWorkspaceQuizMutation: UseMergeWorkspaceQuizzesMutation,
  dispatchMergeQuizDialog: DispatchMergeQuizDialogAction
) {
  return ({
    quizName,
    quizOrder,
  }: {
    quizName: string;
    quizOrder: Array<string>;
  }) => {
    const workspaceId = workspaceItems[0].workspaceId;
    const parentId = (workspaceItems[0] as WorkspaceQuiz).parentFolderId;
    return mergeWorkspaceQuizMutation.mutateAsync(
      {
        workspaceId,
        workspaceQuizIds: quizOrder,
        name: quizName,
        parentId,
      },
      {
        onSuccess: () => {
          toast.positive(translate('Merged quizzes'));
          new CreateQuizEventAdapter({
            creation_method: 'merge',
            number_of_quizzes: 2,
          }).sendAnalytics();
          dispatchMergeQuizDialog({ type: 'CLOSE' });
        },
        onError: (error: unknown) => {
          if (error instanceof Error) {
            apiToaster([], QuizToastActions.MERGE, error);
          }
        },
      }
    );
  };
}

export function useMergeWorkspaceQuizAction(
  dispatchMergeQuizDialog: DispatchMergeQuizDialogAction,
  workspaceId: string
): Action<WorkspaceItem> {
  const mergeWorkspaceQuizMutation = useMergeWorkspaceQuizzes();
  const { permissions } = useWorkspacePermissions(workspaceId);
  const isReadOnly = useIsWorkspaceReadOnly(workspaceId);

  return React.useMemo(
    () => ({
      key: 'merge-quiz',
      title: translate('Merge Quizzes'),
      icon: <IconMerge />,
      isBulkAction: true,
      action: ({ data: workspaceItems, close }) => {
        if (workspaceItems.some((item) => item.type !== 'quiz')) {
          return;
        }
        const itemsToMerge = workspaceItems.map(({ id, name }) => ({
          quizId: id,
          quizName: name,
        }));
        dispatchMergeQuizDialog({
          type: 'OPEN',
          payload: {
            quizzesToMerge: itemsToMerge,
            onMergeQuiz: createOnMerge(
              workspaceItems,
              mergeWorkspaceQuizMutation,
              dispatchMergeQuizDialog
            ),
          },
        });
        close();
      },
      disabled: ({ data: workspaceItems }) =>
        isReadOnly || workspaceItems.length !== 2 || !permissions.createQuiz,
      constraint: ({ data: workspaceItems }) =>
        workspaceItems.every((item) => item.type === 'quiz'),
    }),
    [
      dispatchMergeQuizDialog,
      mergeWorkspaceQuizMutation,
      permissions.createQuiz,
      isReadOnly,
    ]
  );
}
